"use client";

import clsx from "clsx";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import LinkedInIcon from "@/components/icons/LinkedInIcon";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import Logo from "@/components/Logo";
import type { ReactNode } from "react";
import { useLocalePath } from "@/hooks/useLocalePath";
import { getDemoPageKey, withDemoSource } from "@/utils/demoSource";
import {
  useTrackDemoCtaClick,
  type DemoCtaPlacement,
} from "@/hooks/useDemoCtaTracking";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ICON_SIZES, Icon } from "@/components/library/Icon";
import { useAlternateLinks } from "@/hooks/useAlternateLinks";
import { getEnglishPath, getTranslatedPath } from "@/utils/routes";
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@/utils/library/languages";

const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: "English",
  es: "Español",
  pt: "Português",
  fr: "Français",
  ja: "日本語",
  de: "Deutsch",
};

function translateFirstSegment(
  pathWithoutLocale: string,
  fromLang: string,
  toLang: string,
): string {
  const segments = pathWithoutLocale.split("/").filter(Boolean);
  if (segments.length === 0) return "/";
  const first = segments[0];
  const englishKey = getEnglishPath(first, fromLang).slice(1);
  const translated = getTranslatedPath(`/${englishKey}`, toLang);
  return segments.length === 1
    ? translated
    : `${translated}/${segments.slice(1).join("/")}`;
}

type FooterLink = {
  label: string;
  href: string;
  external?: boolean;
  demoCta?: { source: string; placement: DemoCtaPlacement };
};

type FooterColumn = {
  title: string;
  links: FooterLink[];
};

type SocialLink = {
  label: string;
  href: string;
  icon: ReactNode;
};

const LINKEDIN_URL = "https://linkedin.com/company/meetgail/";
const X_URL = "https://x.com/joingail";
const WHATSAPP_URL = "https://wa.me/17868082077";
const TRUST_CENTER_URL = "https://trust.meetgail.com";

function XIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  );
}

export function MarketingFooter() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const trackDemoCtaClick = useTrackDemoCtaClick();
  const footerContactSource = `footer_contact_${getDemoPageKey(pathname)}`;
  const footerDemoSource = `footer_demo_${getDemoPageKey(pathname)}`;

  const pathSegments = pathname.split("/");
  const segment = pathSegments[1];
  const hasLocaleSegment = (SUPPORTED_LANGUAGES as readonly string[]).includes(
    segment,
  );
  const currentLang = hasLocaleSegment
    ? (segment as SupportedLanguage)
    : DEFAULT_LANGUAGE;
  const pathWithoutLocale = hasLocaleSegment
    ? `/${pathSegments.slice(2).join("/")}`
    : pathname;
  const queryString = searchParams.toString();
  const alternates = useAlternateLinks();
  const localizedLanguageLinks = SUPPORTED_LANGUAGES.map((lang) => {
    const alternatePath = alternates.get(lang);
    let localizedPath: string;
    if (alternatePath) {
      localizedPath = alternatePath;
    } else if (pathWithoutLocale === "/") {
      localizedPath = `/${lang}`;
    } else {
      localizedPath = `/${lang}${translateFirstSegment(
        pathWithoutLocale,
        currentLang,
        lang,
      )}`;
    }

    return {
      lang,
      href: queryString ? `${localizedPath}?${queryString}` : localizedPath,
      nativeLabel: LANGUAGE_LABELS[lang],
    };
  });

  const columns: FooterColumn[] = [
    {
      title: t("redesign.footer.groups.product"),
      links: [
        { label: t("redesign.footer.overview"), href: lp("/product") },
        { label: t("header.pricing"), href: lp("/pricing") },
        { label: t("header.compliance"), href: lp("/compliance") },
      ],
    },
    {
      title: t("redesign.footer.groups.company"),
      links: [
        {
          label: t("redesign.footer.contact"),
          href: withDemoSource(lp("/contact-sales"), footerContactSource),
          demoCta: { source: footerContactSource, placement: "footer_contact" },
        },
        { label: t("header.careers"), href: lp("/careers") },
      ],
    },
    {
      title: t("redesign.footer.groups.resources"),
      links: [
        { label: t("header.blog"), href: lp("/blog") },
        {
          label: t("header.trustCenter"),
          href: TRUST_CENTER_URL,
          external: true,
        },
      ],
    },
    {
      title: t("redesign.footer.groups.getStarted"),
      links: [
        { label: t("header.signUp"), href: lp("/signup") },
        {
          label: t("header.requestDemo"),
          href: withDemoSource(lp("/contact-sales"), footerDemoSource),
          demoCta: { source: footerDemoSource, placement: "footer_demo" },
        },
      ],
    },
  ];

  const legalLinks: FooterLink[] = [
    { label: t("redesign.footer.privacy"), href: lp("/privacy-policy") },
    { label: t("redesign.footer.terms"), href: lp("/terms") },
  ];

  const socials: SocialLink[] = [
    {
      label: t("redesign.footer.social.linkedin"),
      href: LINKEDIN_URL,
      icon: <LinkedInIcon className="h-full w-full" aria-hidden="true" />,
    },
    {
      label: t("redesign.footer.social.x"),
      href: X_URL,
      icon: <XIcon className="h-full w-full" />,
    },
    {
      label: t("redesign.footer.social.whatsapp"),
      href: WHATSAPP_URL,
      icon: <WhatsAppIcon className="h-full w-full" />,
    },
  ];

  return (
    <footer className="w-full border-t border-black/10 bg-marketing-surface font-monument">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 md:py-20">
        <div className="grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-4 md:gap-x-12">
          {columns.map((column) => (
            <div key={column.title}>
              <h3 className="font-mono text-xs font-bold uppercase tracking-wide text-gail-gray">
                {column.title}
              </h3>
              <ul className="mt-4 flex flex-col gap-3">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      target={link.external ? "_blank" : undefined}
                      rel={link.external ? "noopener noreferrer" : undefined}
                      onClick={
                        link.demoCta
                          ? () => trackDemoCtaClick(link.demoCta!)
                          : undefined
                      }
                      className="text-sm text-black/70 transition-colors hover:text-black"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col gap-6 border-t border-black/10 pt-8 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-5" />
            <span className="text-xs text-gail-gray">
              {t("footer.copyright")}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
            <nav className="flex items-center gap-6">
              {legalLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-sm text-black/55 transition-colors hover:text-black"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-black/45 transition-colors hover:bg-black/5 hover:text-black"
                >
                  <span className="h-4 w-4">{social.icon}</span>
                </a>
              ))}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t("redesign.footer.chooseLanguage")}
                  className="inline-flex items-center gap-2 rounded-md border border-black/15 bg-white px-3 py-2 text-sm text-black/70 transition-colors hover:border-black/30 hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gail-primary/40"
                >
                  <Icon icon="globe" size={ICON_SIZES.SM} className="opacity-80" />
                  <span>{LANGUAGE_LABELS[currentLang]}</span>
                  <Icon icon="chevron-down" size={ICON_SIZES.XS} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="max-h-72 w-48 overflow-y-auto border-black/10 bg-white text-black shadow-lg"
              >
                {localizedLanguageLinks.map((languageLink) => (
                  <DropdownMenuItem
                    key={languageLink.lang}
                    asChild
                    className="text-black/70 focus:bg-black/5 focus:text-black"
                  >
                    <a
                      href={languageLink.href}
                      lang={languageLink.lang}
                      hrefLang={languageLink.lang}
                      aria-current={
                        languageLink.lang === currentLang ? "page" : undefined
                      }
                      className={clsx(
                        "font-monument",
                        languageLink.lang === currentLang &&
                          "text-gail-primary",
                      )}
                    >
                      {languageLink.nativeLabel}
                    </a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default MarketingFooter;
